/**
 * /sudo/inbox/$id — thread view + compose box + right sidebar.
 *
 * Main column: chronological messages (inbound vs outbound styled
 * differently, internal notes in amber). Compose box at the bottom
 * with a "Private note" toggle that saves the message as a note and
 * skips the outbound send.
 *
 * Sidebar: identity card + other conversations from this person +
 * other conversations from this business + status changer.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import {
  ArrowLeft,
  Download,
  FileText,
  Paperclip,
  Send,
  StickyNote,
  X,
} from 'lucide-react';
import { Button } from '@bookaburra/ui';
import { useDocumentTitle } from '../lib/use-document-title';
import { formatRole } from '../lib/format-role';

const MAX_REPLY_FILES = 5;
const MAX_REPLY_PER_FILE = 4 * 1024 * 1024;
const MAX_REPLY_TOTAL = 4 * 1024 * 1024;

type Status = 'new' | 'reading' | 'replied' | 'closed' | 'spam';
type Channel = 'contact_form' | 'app_contact_form' | 'direct_email';

interface Conversation {
  id: string;
  status: Status;
  channel: Channel;
  name: string | null;
  email: string | null;
  businessName: string | null;
  industry: string | null;
  teamSize: string | null;
  phone: string | null;
  painSummary: string | null;
  sourcePage: string | null;
  userId: string | null;
  tenantId: string | null;
  createdAt: number;
  updatedAt: number;
}

interface Attachment {
  id: string;
  messageId: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt: number;
}

interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  fromEmail: string | null;
  toEmail: string | null;
  subject: string | null;
  bodyText: string;
  isInternalNote: boolean;
  createdAt: number;
  attachments: Attachment[];
}

interface Identity {
  userId: string | null;
  name: string | null;
  email: string;
  signedUp: boolean;
  memberships: Array<{ slug: string; tenantName: string; role: string }>;
}

interface RelatedRow {
  id: string;
  status: Status;
  painSummary: string | null;
  updatedAt: number;
}

interface ConversationDetailResponse {
  conversation: Conversation;
  messages: Message[];
  identity: Identity;
  relatedByUser: RelatedRow[];
  relatedByTenant: RelatedRow[];
}

async function fetchDetail(id: string): Promise<ConversationDetailResponse> {
  const res = await fetch(`/api/super/conversations/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error('detail_failed');
  return res.json();
}

export const Route = createFileRoute('/sudo/inbox/$id')({
  component: ThreadPage,
});

function ThreadPage() {
  const { id } = Route.useParams();
  const query = useQuery({
    queryKey: ['sudo', 'conversations', id],
    queryFn: () => fetchDetail(id),
  });

  useDocumentTitle(
    query.data
      ? `${query.data.conversation.name ?? query.data.conversation.email ?? 'Conversation'} · inbox`
      : 'Inbox · super-admin',
  );

  return (
    <div className="space-y-6">
      <Link
        to="/sudo/inbox"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to inbox
      </Link>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading&hellip;</p>
      ) : query.error || !query.data ? (
        <p className="text-sm text-destructive">Failed to load conversation.</p>
      ) : (
        <ThreadBody data={query.data} />
      )}
    </div>
  );
}

function ThreadBody({ data }: { data: ConversationDetailResponse }) {
  const queryClient = useQueryClient();
  const { conversation, messages, identity, relatedByUser, relatedByTenant } = data;

  const [composerText, setComposerText] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function addFiles(picked: FileList | File[]) {
    setFileError(null);
    const next = [...files];
    let total = next.reduce((a, f) => a + f.size, 0);
    for (const f of Array.from(picked)) {
      if (next.length >= MAX_REPLY_FILES) {
        setFileError(`Up to ${MAX_REPLY_FILES} files.`);
        break;
      }
      if (f.size > MAX_REPLY_PER_FILE) {
        setFileError(`${f.name} is bigger than 4 MB.`);
        continue;
      }
      if (total + f.size > MAX_REPLY_TOTAL) {
        setFileError('Total size over 4 MB (Cloudflare Email limit).');
        break;
      }
      next.push(f);
      total += f.size;
    }
    setFiles(next);
  }

  function removeFile(i: number) {
    setFiles((f) => f.filter((_, idx) => idx !== i));
  }

  const replyMutation = useMutation({
    mutationFn: async (vars: { message: string; isInternalNote: boolean; files: File[] }) => {
      const form = new FormData();
      form.set('message', vars.message);
      form.set('isInternalNote', String(vars.isInternalNote));
      for (const f of vars.files) form.append('attachments', f);
      const res = await fetch(`/api/super/conversations/${conversation.id}/reply`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Failed (${res.status})`);
      }
    },
    onSuccess: () => {
      setComposerText('');
      setIsNote(false);
      setFiles([]);
      setFileError(null);
      queryClient.invalidateQueries({ queryKey: ['sudo', 'conversations', conversation.id] });
      queryClient.invalidateQueries({ queryKey: ['sudo', 'conversations'] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (status: Status) => {
      const res = await fetch(`/api/super/conversations/${conversation.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('status_update_failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sudo', 'conversations', conversation.id] });
      queryClient.invalidateQueries({ queryKey: ['sudo', 'conversations'] });
    },
  });

  function send() {
    const trimmed = composerText.trim();
    if (!trimmed || replyMutation.isPending) return;
    replyMutation.mutate({ message: trimmed, isInternalNote: isNote, files });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[3fr_1fr]">
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-bold tracking-tight">
            {conversation.name || conversation.email || 'Conversation'}
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {conversation.painSummary || ''}
          </p>
        </header>

        <ol className="space-y-3">
          {messages.map((m) => (
            <MessageCard key={m.id} message={m} conversationId={conversation.id} />
          ))}
        </ol>

        {replyMutation.error ? (
          <p className="text-xs text-destructive">
            {(replyMutation.error as Error).message}
          </p>
        ) : null}

        <div className="rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
          <textarea
            rows={6}
            value={composerText}
            onChange={(e) => setComposerText(e.target.value)}
            placeholder={isNote ? 'Private note (only you will see)…' : 'Reply…'}
            className={`w-full resize-y rounded-t-2xl bg-transparent px-4 py-3 text-sm focus-visible:outline-none ${
              isNote ? 'bg-amber-50/60' : ''
            }`}
          />
          {files.length > 0 ? (
            <ul className="space-y-1 border-t border-brand-sage/10 px-4 py-2">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between rounded-md bg-secondary/40 px-2 py-1 text-xs"
                >
                  <span className="truncate">
                    {f.name}{' '}
                    <span className="text-muted-foreground">({formatBytes(f.size)})</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    aria-label={`Remove ${f.name}`}
                    className="ml-2 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {fileError ? (
            <p className="border-t border-amber-300/60 bg-amber-50 px-4 py-2 text-xs text-amber-900">
              {fileError}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-2 border-t border-brand-sage/10 px-4 py-2">
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-brand-ink-soft"
                disabled={isNote}
                title={isNote ? 'Attachments are not sent on private notes' : 'Attach files'}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach
              </button>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={isNote}
                  onChange={(e) => setIsNote(e.target.checked)}
                  className="rounded"
                />
                <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
                Private note
              </label>
            </div>
            <Button
              size="sm"
              disabled={!composerText.trim() || replyMutation.isPending}
              onClick={send}
            >
              {isNote ? (
                <>
                  <StickyNote className="h-3.5 w-3.5" />
                  Save note
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" />
                  Send
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <aside className="space-y-4">
        <IdentityCard identity={identity} />
        <StatusCard
          status={conversation.status}
          onChange={(s) => statusMutation.mutate(s)}
          disabled={statusMutation.isPending}
        />
        <RelatedList title="Other from this person" rows={relatedByUser} currentId={conversation.id} />
        {relatedByTenant.length > 0 ? (
          <RelatedList
            title="Other from this business"
            rows={relatedByTenant}
            currentId={conversation.id}
          />
        ) : null}
      </aside>
    </div>
  );
}

function MessageCard({
  message,
  conversationId,
}: {
  message: Message;
  conversationId: string;
}) {
  const isOutbound = message.direction === 'outbound';
  const isNote = message.isInternalNote;

  const cardClass = isNote
    ? 'border-amber-300/60 bg-amber-50/80'
    : isOutbound
      ? 'border-brand-sage/20 bg-brand-cream-shadow/60'
      : 'border-brand-sage/15 bg-card';

  const images = message.attachments.filter((a) =>
    (a.contentType ?? '').startsWith('image/'),
  );
  const others = message.attachments.filter(
    (a) => !(a.contentType ?? '').startsWith('image/'),
  );

  return (
    <li className={`rounded-2xl border p-4 shadow-sm ${cardClass}`}>
      <header className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {isNote ? (
            <span className="font-semibold text-amber-900">Private note</span>
          ) : isOutbound ? (
            <span className="font-semibold text-brand-sage">You</span>
          ) : (
            <span className="font-semibold">{message.fromEmail ?? 'them'}</span>
          )}
        </span>
        <time>{formatDateTime(message.createdAt)}</time>
      </header>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-brand-ink-soft">
        {message.bodyText}
      </p>

      {images.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {images.map((a) => {
            const src = `/api/super/conversations/${conversationId}/attachments/${a.id}`;
            return (
              <a
                key={a.id}
                href={src}
                target="_blank"
                rel="noreferrer"
                className="block overflow-hidden rounded-md border border-brand-sage/10 hover:opacity-90"
                title={a.filename}
              >
                <img
                  src={src}
                  alt={a.filename}
                  className="aspect-video w-full object-cover"
                  loading="lazy"
                />
              </a>
            );
          })}
        </div>
      ) : null}

      {others.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {others.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between rounded-md bg-secondary/40 px-2 py-1.5 text-xs"
            >
              <span className="flex items-center gap-2 truncate">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="truncate">{a.filename}</span>
                {a.sizeBytes ? (
                  <span className="text-muted-foreground">({formatBytes(a.sizeBytes)})</span>
                ) : null}
              </span>
              <a
                href={`/api/super/conversations/${conversationId}/attachments/${a.id}?download=1`}
                className="inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline"
              >
                <Download className="h-3.5 w-3.5" />
                Download
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function IdentityCard({ identity }: { identity: Identity }) {
  return (
    <section className="rounded-2xl border border-brand-sage/15 bg-card p-4 shadow-brand">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Identity
      </p>
      <p className="mt-2 text-sm font-semibold">{identity.name ?? identity.email}</p>
      <p className="text-xs text-muted-foreground">{identity.email}</p>
      {identity.signedUp ? (
        identity.memberships.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Signed-up user · no business memberships
          </p>
        ) : (
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
            {identity.memberships.map((m) => (
              <li key={m.slug}>
                <span className="font-medium text-brand-ink-soft">{m.tenantName}</span>{' '}
                · {formatRole(m.role as 'owner' | 'manager' | 'staff' | 'client')}
              </li>
            ))}
          </ul>
        )
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">Not signed up.</p>
      )}
    </section>
  );
}

function StatusCard({
  status,
  onChange,
  disabled,
}: {
  status: Status;
  onChange: (s: Status) => void;
  disabled: boolean;
}) {
  const options: Status[] = ['new', 'reading', 'replied', 'closed', 'spam'];
  return (
    <section className="rounded-2xl border border-brand-sage/15 bg-card p-4 shadow-brand">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Status
      </p>
      <select
        value={status}
        onChange={(e) => onChange(e.target.value as Status)}
        disabled={disabled}
        className="mt-2 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </section>
  );
}

function RelatedList({
  title,
  rows,
  currentId,
}: {
  title: string;
  rows: RelatedRow[];
  currentId: string;
}) {
  if (rows.length === 0) {
    return (
      <section className="rounded-2xl border border-brand-sage/15 bg-card p-4 shadow-brand">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">None.</p>
      </section>
    );
  }
  return (
    <section className="rounded-2xl border border-brand-sage/15 bg-card p-4 shadow-brand">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </p>
      <ul className="mt-2 space-y-2">
        {rows.map((r) => (
          <li key={r.id}>
            <Link
              to="/sudo/inbox/$id"
              params={{ id: r.id }}
              className={`block rounded-md px-2 py-1.5 text-xs hover:bg-secondary ${
                r.id === currentId ? 'bg-secondary' : ''
              }`}
            >
              <span className="block font-medium text-brand-ink-soft line-clamp-1">
                {r.painSummary || '(no summary)'}
              </span>
              <span className="block text-muted-foreground">
                {r.status} · {formatRelative(r.updatedAt)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatDateTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatRelative(epochMs: number): string {
  const diff = Date.now() - epochMs;
  if (diff < 0) return 'in the future';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
